import {
  Autocomplete,
  createFilterOptions,
  TextField,
  AutocompleteChangeReason,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FlatAsset } from 'queries/assets/contract';
import { useModal } from '@shape-ds/components';
import { GenericModal, ModalWidthEnum } from 'ui/GenericModal/GenericModal';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useAssetList } from './hooks/useAssetList';

export function CaseAsset() {
  const { t } = useTranslation();
  const assetList = useAssetList();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const { openModal, closeModal } = useModal();

  const filterOptions = createFilterOptions({
    stringify: (option: FlatAsset) => option.name,
    limit: 50,
  });

  const handleAssetChange = (
    selectedAsset: FlatAsset | null,
    reason: AutocompleteChangeReason,
    onChange: (value: FlatAsset | null) => void,
    currentValue: FlatAsset | null,
  ) => {
    const alarms = getValues('alarms') || [];
    const hasAlarms = alarms.length > 0;

    if (
      ((reason === 'selectOption' && selectedAsset && currentValue !== null) ||
        reason === 'clear') &&
      hasAlarms
    ) {
      openModal(
        <GenericModal
          title={t(`case:messages.delete_asset.title`)}
          description={t(`case:messages.delete_asset.message`)}
          width={ModalWidthEnum.MEDIUM}
          leftBtn={{ style: 'secondary', text: t('case:cancel'), onClick: closeModal }}
          rightBtn={{
            style: 'primary',
            text: t('case:continue_anyway'),
            onClick: () => {
              onChange(selectedAsset);
              setValue('alarms', []);
              closeModal();
            },
          }}
        />,
      );
    } else {
      onChange(selectedAsset);
    }
  };

  return (
    <div className="asset-select" data-testid="asset-select">
      <Controller
        name="asset"
        control={control}
        rules={{
          required: t('components:authorized_users.modal.required_field'),
          validate: (value) => {
            return !!value?.id || t('components:authorized_users.modal.required_field');
          },
        }}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            onChange={(event, selectedAsset, reason) =>
              handleAssetChange(selectedAsset, reason as AutocompleteChangeReason, onChange, value)
            }
            value={value}
            options={assetList}
            filterOptions={filterOptions}
            popupIcon={<KeyboardArrowDownRoundedIcon />}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                name="asset"
                placeholder={t('case:placeholders.selectAsset')}
                size="small"
                data-testid="select-asset"
                variant="outlined"
                error={!!errors.asset}
                helperText={errors?.asset?.message?.toString()}
                required
              />
            )}
          />
        )}
      />
    </div>
  );
}
